import React from "react";
import { BookingLabelsEnum } from "../../../../../../types/bookingEnums";
import { VenueConfig } from "../../../../../../types/venues";
import { VenueGroupConfig } from "../../../../../../types/VenueGroup";
import {
  getAllBookingLabels,
  getAllCustomerLabels
} from "../../../SelectedBooking/Labels/BookingLabels";
import { PillsWithOverflow } from "./PillsWithOverflow";

interface BookingLabelsProps {
  booking: any;
  venueConfig: VenueConfig;
  venueGroupConfig: VenueGroupConfig;
  maxPills?: number;
  isOnRunSheet?: boolean;
}

const getBookingLabelColor = (
  labelId: string,
  venueConfig: VenueConfig,
  venueGroupConfig: VenueGroupConfig
): { backgroundColour: string; labelColour: string } => {
  const allLabels = getAllBookingLabels({ venueConfig, venueGroupConfig });
  const label = allLabels.find((l) => l.id === labelId);

  if (label?.colour) {
    const isLightTheme =
      document.documentElement.getAttribute("data-theme") === "Light";
    const baseColor = `#${label.colour}`;

    if (
      isLightTheme &&
      (baseColor.toLowerCase().includes("ff") ||
        baseColor.toLowerCase().includes("f"))
    ) {
      const colorValue = parseInt(label.colour, 16);
      if (colorValue > 0xffcc00 && colorValue < 0xffff99) {
        return {
          backgroundColour: "#fbbf24",
          labelColour: "#451a03"
        };
      }
    }

    return {
      backgroundColour: baseColor,
      labelColour: isLightTheme ? "#000000" : "#ffffff"
    };
  }

  const isLightTheme =
    document.documentElement.getAttribute("data-theme") === "Light";

  if (labelId === "auto_confirmed")
    return {
      backgroundColour: isLightTheme ? "#a7f3d0" : "#065f46",
      labelColour: isLightTheme ? "#065f46" : "#a7f3d0"
    };
  if (labelId === "linked_booking")
    return {
      backgroundColour: isLightTheme ? "#bfdbfe" : "#1e40af",
      labelColour: isLightTheme ? "#1e40af" : "#bfdbfe"
    };

  return {
    backgroundColour: isLightTheme ? "#e5e7eb" : "#4b5563",
    labelColour: isLightTheme ? "#374151" : "#e5e7eb"
  };
};

const getCustomerLabelColor = (
  labelId: string,
  venueConfig: VenueConfig,
  venueGroupConfig: VenueGroupConfig
): { backgroundColour: string; labelColour: string } => {
  const allLabels = getAllCustomerLabels({ venueConfig, venueGroupConfig });
  const label = allLabels.find((l) => l.id === labelId);

  if (label?.colour) {
    const isLightTheme =
      document.documentElement.getAttribute("data-theme") === "Light";
    return {
      backgroundColour: `#${label.colour}`,
      labelColour: isLightTheme ? "#000000" : "#ffffff"
    };
  }

  const isLightTheme =
    document.documentElement.getAttribute("data-theme") === "Light";
  return {
    backgroundColour: isLightTheme ? "#d1fae5" : "#065f46",
    labelColour: isLightTheme ? "#065f46" : "#d1fae5"
  };
};

export const BookingLabels: React.FC<BookingLabelsProps> = ({
  booking,
  venueConfig,
  venueGroupConfig,
  maxPills,
  isOnRunSheet = false
}) => {
  const pills = [];

  // For runsheet beta, skip all labels since we use icons instead
  if (isOnRunSheet) {
    return <PillsWithOverflow pills={[]} maxPills={maxPills} />;
  }

  booking.labels?.forEach((label: string, index: number) => {
    // Skip linked_booking label from left sidebar
    if (label === "linked_booking") {
      return;
    }

    if (label === BookingLabelsEnum.LinkedBooking) {
      const colors = getBookingLabelColor(
        "linked_booking",
        venueConfig,
        venueGroupConfig
      );
      pills.push({
        key: `label-${index}`,
        label: "Linked",
        backgroundColour: colors.backgroundColour,
        labelColour: colors.labelColour
      });
    } else {
      const colors = getBookingLabelColor(label, venueConfig, venueGroupConfig);
      pills.push({
        key: `label-${index}`,
        label: label
          .replace(/_/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase()),
        backgroundColour: colors.backgroundColour,
        labelColour: colors.labelColour
      });
    }
  });

  booking.customer_labels?.forEach((customerLabel: string, index: number) => {
    const colors = getCustomerLabelColor(
      customerLabel,
      venueConfig,
      venueGroupConfig
    );
    pills.push({
      key: `customer-label-${index}`,
      label: customerLabel
        .replace(/_/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase()),
      backgroundColour: colors.backgroundColour,
      labelColour: colors.labelColour
    });
  });

  const isLightTheme =
    document.documentElement.getAttribute("data-theme") === "Light";

  if (booking.doubleConfirmed && !isOnRunSheet) {
    pills.push({
      key: "double-confirmed",
      label: "Double Confirmed",
      backgroundColour: isLightTheme ? "#ddd6fe" : "#5b21b6",
      labelColour: isLightTheme ? "#5b21b6" : "#ddd6fe"
    });
  }

  if ((booking.linkedBookings?.length ?? 0) !== 0 && !isOnRunSheet) {
    pills.push({
      key: "linked-bookings",
      label: "Linked",
      backgroundColour: isLightTheme ? "#bfdbfe" : "#1e40af",
      labelColour: isLightTheme ? "#1e40af" : "#bfdbfe"
    });
  }

  if (booking.hasPreorders && !isOnRunSheet) {
    pills.push({
      key: "preorders",
      label: "Pre-orders",
      backgroundColour: isLightTheme ? "#e5e7eb" : "#6b7280",
      labelColour: isLightTheme ? "#374151" : "#ffffff"
    });
  }

  if ((booking.deposits?.length ?? 0) !== 0 && !isOnRunSheet) {
    pills.push({
      key: "deposits",
      label: "Deposits",
      backgroundColour: isLightTheme ? "#e5e7eb" : "#6b7280",
      labelColour: isLightTheme ? "#374151" : "#ffffff"
    });
  }

  if (booking.company?.[0] && !isOnRunSheet) {
    pills.push({
      key: "company",
      label: "Company",
      backgroundColour: isLightTheme ? "#e5e7eb" : "#4b5563",
      labelColour: isLightTheme ? "#374151" : "#e5e7eb"
    });
  }

  return (
    <div className="DiarySidebarListItem__labels">
      <PillsWithOverflow pills={pills} maxHeight={120} maxPills={maxPills} />
    </div>
  );
};
